import { useEffect, useState } from "react";

/**
 * Motion + capability preferences hook.
 *
 * Returns a stable object with:
 *   - prefersReducedMotion: honors OS-level accessibility setting.
 *   - isMobile: coarse pointer or narrow viewport.
 *   - isTouch: hasPointer:coarse or has touch events.
 *   - isLowPower: heuristics (deviceMemory <= 4 GB, hardwareConcurrency <= 4).
 *     Only relevant for gating WebGL/heavy animations, never for showing content.
 *
 * All 3D components must degrade gracefully when any of these are true.
 */
const DEFAULT = {
  prefersReducedMotion: false,
  isMobile: false,
  isTouch: false,
  isLowPower: false,
};

function detect() {
  if (typeof window === "undefined") return DEFAULT;
  const mq = (q) => (window.matchMedia ? window.matchMedia(q) : { matches: false });

  const prefersReducedMotion = mq("(prefers-reduced-motion: reduce)").matches;
  const isMobile = mq("(max-width: 767px)").matches;
  const isTouch = mq("(pointer: coarse)").matches || "ontouchstart" in window;

  let isLowPower = false;
  try {
    const nav = window.navigator || {};
    const mem = typeof nav.deviceMemory === "number" ? nav.deviceMemory : 8;
    const cores = typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : 8;
    if (mem <= 4 && cores <= 4) isLowPower = true;
  } catch {
    /* ignore */
  }

  return { prefersReducedMotion, isMobile, isTouch, isLowPower };
}

export default function useMotionPreferences() {
  const [prefs, setPrefs] = useState(detect);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const queries = [
      window.matchMedia("(prefers-reduced-motion: reduce)"),
      window.matchMedia("(max-width: 767px)"),
      window.matchMedia("(pointer: coarse)"),
    ];
    const update = () => setPrefs(detect());
    queries.forEach((q) => q.addEventListener?.("change", update));
    update();
    return () => queries.forEach((q) => q.removeEventListener?.("change", update));
  }, []);

  return prefs;
}
